import { FC, useCallback, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthSession, AccountContext } from 'auth/accountProvider';
import LpiSummary from './LpiSummary';
import LpiResultsTable from './LpiResultsTable';
// Hidden for the client demo (2026-09-15). The component and its cards are
// untouched — restore this import and the element below to bring it back.
// import LpiLookup from './LpiLookup';
import LpiFilterBar from './LpiFilterBar';
import useStuckSentinel from '../../hooks/useStuckSentinel';
import LpiTabBar from './LpiTabBar';
import LpiCoverageChart from './LpiCoverageChart';
import LpiBarChart from './LpiBarChart';
import { useLpiAggregates } from './useLpiAggregates';
import { monthRange, monthBounds, ytdRange } from './monthRange';
import {
  LpiResponse,
  LpiFilters,
  LpiPagination,
  LpiReportsTab,
  ClientListResponse,
  VehicleListResponse,
  VehicleTypeListResponse,
  ModalityDetailListResponse,
  CarrierListResponse,
} from './types';
import './LpiReports.scss';

// Helper to safely get session tokens with validation
interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

const getSessionTokens = async (
  getSession: () => Promise<AuthSession>
): Promise<SessionTokens> => {
  const session = await getSession();

  if (!session) {
    throw new Error('Session expired. Please log in again.');
  }

  const accessToken = session.getAccessToken?.();
  const idToken = session.getIdToken?.();

  if (!accessToken || !idToken) {
    throw new Error('Invalid session. Please log in again.');
  }

  const jwtToken = accessToken.getJwtToken?.();
  const sessionId = idToken.payload?.sessionId;

  if (!jwtToken || !sessionId) {
    throw new Error('Session tokens missing. Please log in again.');
  }

  return { jwtToken, sessionId };
};

/** Every filter at rest. Dates are supplied separately by the caller. */
const emptyLpiFilters = (): LpiFilters => ({
  client_id: [],
  start_date: '',
  end_date: '',
  vehicle_id: [],
  vehicle_type: [],
  modality_detail: [],
  carrier_id: [],
  transport_order_number: [],
  lpi_only: false,
});

/** The default period: year to date. */
const ytdDates = (): Pick<LpiFilters, 'start_date' | 'end_date'> => {
  const { start, end } = ytdRange();
  return { start_date: start, end_date: end };
};

const LpiReports: FC = () => {
  const { t } = useTranslation();
  const { getSession } = useContext(AccountContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Top-level tab, seeded from ?tab= so the view is linkable. Mirrors the
  // CpiReports pattern: 'dashboard' is the default and carries no parameter.
  const [activeTab, setActiveTab] = useState<LpiReportsTab>(
    () => (searchParams.get('tab') === 'details' ? 'details' : 'dashboard')
  );

  const handleTabChange = (tab: LpiReportsTab) => {
    setActiveTab(tab);
    // Seed from the router's params, not window.location, so any sibling
    // query parameters survive and the handler works under MemoryRouter.
    const params = new URLSearchParams(searchParams);
    if (tab === 'dashboard') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [lpiResults, setLpiResults] = useState<LpiResponse | null>(null);
  const [calculationError, setCalculationError] = useState<string>('');
  const [showNoDataMessage, setShowNoDataMessage] = useState<boolean>(false);

  // Export state
  const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
  const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [pagination, setPagination] = useState<LpiPagination | null>(null);

  // Filter state. Defaults to year-to-date rather than all-time: an unbounded
  // query is both the slowest thing this page can run and rarely what someone
  // opening it wants to see. Lazy initializer so the range is computed once on
  // mount instead of on every render.
  // Condenses the sticky filter bar once it pins to the top of the viewport.
  const { sentinelRef, isStuck } = useStuckSentinel();

  const [filters, setFilters] = useState<LpiFilters>(() => ({
    ...emptyLpiFilters(),
    ...ytdDates(),
  }));
  // Snapshot of the filters actually applied to the results table/summary
  // (updated only inside handleApplyFilters/handleClearFilters). The
  // dashboard charts must key off this, not the draft `filters` state above
  // — otherwise every keystroke/tick in LpiFilterBar (which calls bare
  // setFilters with no debounce) fires three aggregate GROUP BY requests for
  // a filter set the user has not applied yet, and the charts briefly show
  // data for a different filter set than the KPI cards above them.
  const [appliedFilters, setAppliedFilters] = useState<LpiFilters>(filters);
  const [clients, setClients] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
  const [modalityDetails, setModalityDetails] = useState<string[]>([]);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState<boolean>(false);
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState<boolean>(false);
  const [isLoadingModalityDetails, setIsLoadingModalityDetails] = useState<boolean>(false);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState<boolean>(false);
  const [clientsLoaded, setClientsLoaded] = useState<boolean>(false);
  const [vehiclesLoaded, setVehiclesLoaded] = useState<boolean>(false);
  const [vehicleTypesLoaded, setVehicleTypesLoaded] = useState<boolean>(false);
  const [modalityDetailsLoaded, setModalityDetailsLoaded] = useState<boolean>(false);
  const [carriersLoaded, setCarriersLoaded] = useState<boolean>(false);

  // Wrapped in useCallback for defence in depth: useLpiAggregates already
  // ref-stabilizes the getter it's given internally, but passing a stable
  // reference here documents the intent and avoids relying on that alone.
  const getAggregateTokens = useCallback(
    () => getSessionTokens(getSession),
    [getSession]
  );

  // Dashboard charts fetch only once a calculation has produced results —
  // the same gate the results table already uses — so the Dashboard tab
  // does not fire four requests before the user has applied any filters.
  const {
    coverage,
    monthly,
    byVehicle,
    byClient,
    byOrder,
    isLoading: isLoadingAggregates,
    error: aggregatesError,
    refetch: refetchAggregates,
  } = useLpiAggregates(appliedFilters, getAggregateTokens, lpiResults !== null);

  // Continuous month axis for the monthly bar chart: memoized so its
  // identity is stable across renders when the date range hasn't changed,
  // which keeps LpiBarChart's own option memo (keyed on `categories`) from
  // being invalidated every render. Keyed on appliedFilters, matching the
  // hook above, so the axis and the buckets it's paired with always
  // describe the same filter set.
  const monthCategories = useMemo(
    () => monthRange(appliedFilters.start_date, appliedFilters.end_date),
    [appliedFilters.start_date, appliedFilters.end_date]
  );

  // Fetch clients list (lazy loaded on dropdown open)
  const fetchClients = async () => {
    if (clientsLoaded || isLoadingClients) return;
    setIsLoadingClients(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const response = await axios.get<ClientListResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/lpi/clients/list`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );

      if (response.status === 200 && response.data) {
        setClients(response.data.clients);
        setClientsLoaded(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Fetch vehicles list (lazy loaded on dropdown open)
  const fetchVehicles = async () => {
    if (vehiclesLoaded || isLoadingVehicles) return;
    setIsLoadingVehicles(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const response = await axios.get<VehicleListResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/lpi/vehicles/list`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );

      if (response.status === 200 && response.data) {
        // Normalize to plain ID strings: the dropdown renders each option as a
        // React child, so an object option would throw React error #31.
        const vehicleIds = (response.data.vehicles ?? []).map((v) =>
          typeof v === 'string' ? v : v.vehicleId
        );
        setVehicles(vehicleIds);
        setVehiclesLoaded(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch vehicles:', error);
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  // Fetch vehicle types list (lazy loaded on dropdown open)
  const fetchVehicleTypes = async () => {
    if (vehicleTypesLoaded || isLoadingVehicleTypes) return;
    setIsLoadingVehicleTypes(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const response = await axios.get<VehicleTypeListResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/lpi/vehicle-types/list`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );

      if (response.status === 200 && response.data) {
        setVehicleTypes(response.data.vehicle_types);
        setVehicleTypesLoaded(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch vehicle types:', error);
    } finally {
      setIsLoadingVehicleTypes(false);
    }
  };

  // Fetch modality details list (lazy loaded on dropdown open). The options are
  // scoped to the currently-applied date range, so we track the range the list
  // was loaded for and refetch when it changes (see modalityDetailsDateKey).
  const [modalityDetailsDateKey, setModalityDetailsDateKey] = useState<string>('');
  const fetchModalityDetails = async () => {
    const dateKey = `${filters.start_date}|${filters.end_date}`;
    if (isLoadingModalityDetails) return;
    if (modalityDetailsLoaded && modalityDetailsDateKey === dateKey) return;
    setIsLoadingModalityDetails(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const params = new URLSearchParams();
      if (filters.start_date) params.append('startDate', filters.start_date);
      if (filters.end_date) params.append('endDate', filters.end_date);
      const queryString = params.toString();

      const response = await axios.get<ModalityDetailListResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/lpi/modality-details/list${
          queryString ? `?${queryString}` : ''
        }`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );

      if (response.status === 200 && response.data) {
        setModalityDetails(response.data.modality_details);
        setModalityDetailsLoaded(true);
        setModalityDetailsDateKey(dateKey);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch modality details:', error);
    } finally {
      setIsLoadingModalityDetails(false);
    }
  };

  // Fetch carriers list (lazy loaded on dropdown open)
  const fetchCarriers = async () => {
    if (carriersLoaded || isLoadingCarriers) return;
    setIsLoadingCarriers(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const response = await axios.get<CarrierListResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/lpi/carriers/list`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );

      if (response.status === 200 && response.data) {
        setCarriers(response.data.carriers);
        setCarriersLoaded(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch carriers:', error);
    } finally {
      setIsLoadingCarriers(false);
    }
  };

  // Build query string from filters and pagination
  // `f` defaults to current state, but callers can pass an explicit filter set
  // to avoid stale-closure reads when fetching right after a state change
  // (e.g. the LPI-only toggle refetches immediately with its new value).
  const buildQueryString = (page: number, limit: number, f: LpiFilters = filters) => {
    const params = new URLSearchParams();

    // Pagination parameters
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (f.client_id.length > 0) {
      params.append('client_id', f.client_id.join(','));
    }
    if (f.start_date) {
      params.append('start_date', f.start_date);
    }
    if (f.end_date) {
      params.append('end_date', f.end_date);
    }
    if (f.vehicle_id.length > 0) {
      params.append('vehicle_id', f.vehicle_id.join(','));
    }
    if (f.vehicle_type.length > 0) {
      params.append('vehicle_type', f.vehicle_type.join(','));
    }
    if (f.modality_detail.length > 0) {
      params.append('modality_detail', f.modality_detail.join(','));
    }
    if (f.carrier_id.length > 0) {
      params.append('carrier_id', f.carrier_id.join(','));
    }
    if (f.transport_order_number.length > 0) {
      params.append(
        'transport_order_number',
        f.transport_order_number.join(',')
      );
    }
    if (f.lpi_only) {
      params.append('lpi_only', 'true');
    }

    return params.toString();
  };

  // Serialize the current filters (no pagination) for the export endpoints.
  const appendExportFilterParams = (params: URLSearchParams) => {
    if (filters.client_id.length > 0) params.append('client_id', filters.client_id.join(','));
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.vehicle_id.length > 0) params.append('vehicle_id', filters.vehicle_id.join(','));
    if (filters.vehicle_type.length > 0) params.append('vehicle_type', filters.vehicle_type.join(','));
    if (filters.modality_detail.length > 0) params.append('modality_detail', filters.modality_detail.join(','));
    if (filters.carrier_id.length > 0) params.append('carrier_id', filters.carrier_id.join(','));
    if (filters.lpi_only) params.append('lpi_only', 'true');
  };

  // Download the LPI order list (respecting all active filters + the toggle) as
  // Excel or CSV from the server-side export endpoint.
  const downloadExport = async (format: 'xlsx' | 'csv') => {
    const { jwtToken, sessionId } = await getSessionTokens(getSession);
    const headers = {
      Authorization: `Session ${jwtToken}`,
      'X-Session-Id': sessionId,
    };
    const apiBase = process.env.REACT_APP_LPI_API_URL;

    // Preflight: for Excel, block if the dataset exceeds the row limit.
    const countParams = new URLSearchParams();
    appendExportFilterParams(countParams);
    const countRes = await fetch(`${apiBase}/lpi/export/count?${countParams}`, { headers });
    if (countRes.ok) {
      const countData = await countRes.json();
      if (format === 'xlsx' && countData.excel_supported === false) {
        // eslint-disable-next-line no-alert
        window.alert(
          t('Too many rows for Excel. Please use CSV or narrow your filters.')
        );
        return;
      }
    }

    const params = new URLSearchParams();
    appendExportFilterParams(params);
    params.append('format', format);

    const response = await fetch(`${apiBase}/lpi/export?${params}`, { headers });
    if (!response.ok) {
      throw new Error(`Export failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp =
      filters.start_date && filters.end_date
        ? `${filters.start_date}_to_${filters.end_date}`
        : 'all';
    a.download = `LPI_orders_${stamp}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      await downloadExport('xlsx');
    } catch (error) {
      console.error('LPI Excel export failed:', error);
      // eslint-disable-next-line no-alert
      window.alert(t('Unable to export. Please try again later.'));
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportCsv = async () => {
    setIsExportingCsv(true);
    try {
      await downloadExport('csv');
    } catch (error) {
      console.error('LPI CSV export failed:', error);
      // eslint-disable-next-line no-alert
      window.alert(t('Unable to export. Please try again later.'));
    } finally {
      setIsExportingCsv(false);
    }
  };

  // Sync filters with URL (without pagination params for cleaner URLs)
  const syncFiltersToURL = () => {
    const params = new URLSearchParams();
    if (filters.client_id.length > 0) {
      params.append('client_id', filters.client_id.join(','));
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.vehicle_id.length > 0) {
      params.append('vehicle_id', filters.vehicle_id.join(','));
    }
    if (filters.vehicle_type.length > 0) {
      params.append('vehicle_type', filters.vehicle_type.join(','));
    }
    if (filters.modality_detail.length > 0) {
      params.append('modality_detail', filters.modality_detail.join(','));
    }
    if (filters.carrier_id.length > 0) {
      params.append('carrier_id', filters.carrier_id.join(','));
    }
    if (filters.transport_order_number.length > 0) {
      params.append(
        'transport_order_number',
        filters.transport_order_number.join(',')
      );
    }
    if (activeTab !== 'dashboard') {
      params.append('tab', activeTab);
    }
    const queryString = params.toString();
    setSearchParams(queryString ? `?${queryString}` : '');
  };

  const handleCalculateLPI = async (
    page: number = currentPage,
    limit: number = pageSize,
    f: LpiFilters = filters
  ) => {
    setIsCalculating(true);
    setCalculationError('');
    setShowNoDataMessage(false);

    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const queryString = buildQueryString(page, limit, f);
      const url = `${process.env.REACT_APP_LPI_API_URL}/lpi/calculate?${queryString}`;

      const response = await axios.get<LpiResponse>(url, {
        headers: {
          Authorization: `Session ${jwtToken}`,
          'X-Session-Id': sessionId,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        // Treat 304 (Not Modified) as success so cached responses still render
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 304,
      });

      const hasOrders = response.data?.orders?.length;

      if (hasOrders && response.data) {
        setLpiResults(response.data);
        setCalculationError('');
        setShowNoDataMessage(false);
        // Update pagination state from response
        if (response.data.pagination) {
          setPagination(response.data.pagination);
          setCurrentPage(response.data.pagination.page);
        }
      } else {
        // No data available
        setLpiResults(null);
        setPagination(null);
        setShowNoDataMessage(true);
        setCalculationError('');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        setCalculationError(error.response.data.message);
      } else {
        setCalculationError(
          t('Unable to calculate LPI. Please try again later.')
        );
      }
      setLpiResults(null);
      setPagination(null);
      setShowNoDataMessage(false);
    } finally {
      setIsCalculating(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    handleCalculateLPI(newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    handleCalculateLPI(1, newPageSize);
  };

  // Filter handlers
  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    syncFiltersToURL();
    setCurrentPage(1); // Reset to first page when filters change
    handleCalculateLPI(1, pageSize);
  };

  const handleClearFilters = () => {
    // Clear returns the page to its default state, and the default is
    // year-to-date — not all-time. Emptying the dates here would make the very
    // next Apply the most expensive query the page can issue.
    const emptyFilters: LpiFilters = {
      ...emptyLpiFilters(),
      ...ytdDates(),
    };
    setFilters(emptyFilters);
    // Set from emptyFilters, not `filters` — setFilters above hasn't taken
    // effect yet within this handler, so reading `filters` here would
    // re-apply the stale draft.
    setAppliedFilters(emptyFilters);
    const params = new URLSearchParams();
    if (activeTab !== 'dashboard') {
      params.set('tab', activeTab);
    }
    setSearchParams(params);
    setCurrentPage(1); // Reset to first page when filters are cleared
    handleCalculateLPI(1, pageSize, emptyFilters);
  };

  /**
   * Drill-through from a dashboard chart into the Details list.
   *
   * Applies the patch on top of the current filters and commits it in one go —
   * draft, applied snapshot, URL and results all move together, because the
   * user never sees the Apply button in this flow. It deliberately mirrors
   * handleApplyFilters rather than calling it: that reads `filters` from state,
   * which has not yet taken effect inside this handler (the same staleness
   * handleClearFilters documents), so the merged value has to be threaded
   * through explicitly.
   */
  const handleChartDrill = (patch: Partial<LpiFilters>) => {
    const updated: LpiFilters = { ...filters, ...patch };

    setFilters(updated);
    setAppliedFilters(updated);
    setActiveTab('details');

    const params = new URLSearchParams();
    if (updated.client_id.length > 0) {
      params.append('client_id', updated.client_id.join(','));
    }
    if (updated.start_date) params.append('start_date', updated.start_date);
    if (updated.end_date) params.append('end_date', updated.end_date);
    if (updated.vehicle_id.length > 0) {
      params.append('vehicle_id', updated.vehicle_id.join(','));
    }
    if (updated.vehicle_type.length > 0) {
      params.append('vehicle_type', updated.vehicle_type.join(','));
    }
    if (updated.modality_detail.length > 0) {
      params.append('modality_detail', updated.modality_detail.join(','));
    }
    if (updated.carrier_id.length > 0) {
      params.append('carrier_id', updated.carrier_id.join(','));
    }
    if (updated.transport_order_number.length > 0) {
      params.append(
        'transport_order_number',
        updated.transport_order_number.join(',')
      );
    }
    params.append('tab', 'details');
    setSearchParams(params);

    setCurrentPage(1);
    handleCalculateLPI(1, pageSize, updated);
  };

  // A month bucket ('2025-11') is an axis label, not a filter value: it has to
  // become the date range that month spans. A malformed label yields no bounds
  // and is ignored rather than producing a filter built from NaN.
  const handleMonthDrill = (bucket: string) => {
    const bounds = monthBounds(bucket);
    if (!bounds) return;
    handleChartDrill({ start_date: bounds.start, end_date: bounds.end });
  };

  // Toggle "show only orders with LPI". Refetches immediately with the new value
  // (page reset to 1) rather than waiting for the Apply button.
  const handleToggleLpiOnly = () => {
    const updated: LpiFilters = { ...filters, lpi_only: !filters.lpi_only };
    setFilters(updated);
    setCurrentPage(1);
    handleCalculateLPI(1, pageSize, updated);
  };

  return (
    <div className="lpi-reports container">
      {/* The visible page title is gone — the nav item already names this page.
          The heading stays for screen readers, where heading navigation is how
          people establish where they are. */}
      <h1 className="lpi-reports__sr-only">{t('LPI Reports')}</h1>

      {/* Zero-height marker the stuck-detection hook watches. It has to sit
          outside the bar: once the bar pins it stops moving, so only something
          above it can tell us we have scrolled past. */}
      <div ref={sentinelRef} className="lpi-reports__filter-sentinel" aria-hidden="true" />

      {/* Filter Bar */}
      <LpiFilterBar
        isStuck={isStuck}
        filters={filters}
        onFiltersChange={setFilters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        clients={clients}
        vehicles={vehicles}
        vehicleTypes={vehicleTypes}
        modalityDetails={modalityDetails}
        carriers={carriers}
        isLoading={isCalculating}
        isLoadingClients={isLoadingClients}
        isLoadingVehicles={isLoadingVehicles}
        isLoadingVehicleTypes={isLoadingVehicleTypes}
        isLoadingModalityDetails={isLoadingModalityDetails}
        isLoadingCarriers={isLoadingCarriers}
        onClientsOpen={fetchClients}
        onVehiclesOpen={fetchVehicles}
        onVehicleTypesOpen={fetchVehicleTypes}
        onModalityDetailsOpen={fetchModalityDetails}
        onCarriersOpen={fetchCarriers}
      />

      {/* Calculation Error Display */}
      {calculationError && (
        <div
          role="alert"
          className="alert alert-danger alert-sm alert-dismissible"
          data-testid="calculation-error"
        >
          {calculationError}
        </div>
      )}

      <LpiTabBar activeTab={activeTab} onTabChange={handleTabChange} />

      {/* No Data Message — shared: describes the result set, not a tab */}
      {showNoDataMessage && (
        <div className="lpi-reports__no-data-section">
          <div className="lpi-reports__no-data-message">
            <span className="lpi-reports__no-data-icon">📊</span>
            <h3>{t('No Data Available')}</h3>
            <p>
              {t(
                'There is no LPI data available for the selected filters.'
              )}
            </p>
          </div>
        </div>
      )}

      {/* Dashboard Tab — KPI cards plus the Phase 4 aggregate charts */}
      <div
        data-testid="lpi-tab-dashboard"
        className={`lpi-reports__tab-content ${
          activeTab !== 'dashboard' ? 'lpi-reports__tab-content--hidden' : ''
        }`}
      >
        {/* Empty state: before any calculation there is nothing to chart, so
            say what to do rather than showing a blank tab. Hidden while a
            calculation is running so it doesn't flash between states. */}
        {!lpiResults && !isCalculating && (
          <div
            className="lpi-reports__dashboard-placeholder"
            data-testid="lpi-dashboard-placeholder"
          >
            <span
              className="lpi-reports__dashboard-placeholder-icon"
              aria-hidden="true"
            >
              📈
            </span>
            <h3>{t('lpi.dashboard.placeholder.title')}</h3>
            <p>{t('lpi.dashboard.placeholder.body')}</p>
          </div>
        )}

        {/* Mounts during calculation too, so the KPI row holds its shape with
            skeletons instead of popping in — matching the CPI summary cards.
            The placeholder above is already hidden while isCalculating. */}
        <LpiSummary
          summary={lpiResults?.summary ?? null}
          isLoading={isCalculating}
        />

        {/* Gated on lpiResults, matching LpiSummary above — before any
            calculation the noData branch inside each chart can't fire (it
            needs total === 0 / buckets.length === 0, not null), so an
            ungated grid rendered three bordered cards with a title and
            nothing else. */}
        {lpiResults && (
          <div className="lpi-reports__charts">
            <LpiCoverageChart
              data={coverage}
              isLoading={isLoadingAggregates}
              error={aggregatesError}
              onRetry={refetchAggregates}
            />
            {/* Order matters: the grid is two columns and coverage takes the
                first cell, so by-vehicle completes row 1 and the two per-month
                charts pair up on row 2 — load performance beside utilisation
                rate, which is the comparison the dashboard is read for. */}
            <LpiBarChart
              titleKey="lpi.chart.byVehicle.title"
              buckets={byVehicle}
              isLoading={isLoadingAggregates}
              error={aggregatesError}
              onRetry={refetchAggregates}
              onBucketClick={(bucket) =>
                handleChartDrill({ vehicle_id: [bucket] })
              }
            />
            <LpiBarChart
              titleKey="lpi.chart.monthly.title"
              buckets={monthly}
              categories={monthCategories}
              isLoading={isLoadingAggregates}
              error={aggregatesError}
              onRetry={refetchAggregates}
              onBucketClick={handleMonthDrill}
            />
            <LpiBarChart
              titleKey="lpi.chart.monthlyBenuttingsgraad.title"
              metric="benuttingsgraad"
              buckets={monthly}
              categories={monthCategories}
              isLoading={isLoadingAggregates}
              error={aggregatesError}
              onRetry={refetchAggregates}
              onBucketClick={handleMonthDrill}
            />
            <LpiBarChart
              titleKey="lpi.chart.byClient.title"
              buckets={byClient}
              isLoading={isLoadingAggregates}
              error={aggregatesError}
              onRetry={refetchAggregates}
              onBucketClick={(bucket) =>
                handleChartDrill({ client_id: [bucket] })
              }
            />
            {/* Pairs with by-client on the third row. Unlike the others this
                one is always a top-N: orders are unbounded, so its title
                states the cap rather than implying a complete picture. */}
            <LpiBarChart
              titleKey="lpi.chart.byOrder.title"
              buckets={byOrder}
              isLoading={isLoadingAggregates}
              error={aggregatesError}
              onRetry={refetchAggregates}
              onBucketClick={(bucket) =>
                handleChartDrill({ transport_order_number: [bucket] })
              }
            />
          </div>
        )}
      </div>

      {/* Details Tab — the list view and the order lookup */}
      <div
        data-testid="lpi-tab-details"
        className={`lpi-reports__tab-content ${
          activeTab !== 'details' ? 'lpi-reports__tab-content--hidden' : ''
        }`}
      >
        {/* Results actions: the LPI-only toggle and the export buttons.
            The toggle renders whenever a calculation has run (results OR no-data)
            so the user can always switch back — even if "LPI only" yields nothing.
            Export only shows when there is data to export. */}
        {(lpiResults || showNoDataMessage) && (
          <div className="lpi-reports__results-actions">
            <label className="lpi-reports__toggle" htmlFor="lpi-reports-lpi-only-toggle">
              <input
                id="lpi-reports-lpi-only-toggle"
                type="checkbox"
                checked={filters.lpi_only}
                onChange={handleToggleLpiOnly}
                disabled={isCalculating}
              />
              <span className="lpi-reports__toggle-slider" aria-hidden="true" />
              <span className="lpi-reports__toggle-label">
                {t('Show only orders with LPI')}
              </span>
            </label>

            {lpiResults && (
              <div className="lpi-reports__export">
                <button
                  type="button"
                  className="lpi-reports__export-btn"
                  onClick={handleExportExcel}
                  disabled={isExportingExcel || isExportingCsv || isCalculating}
                >
                  {isExportingExcel ? t('Exporting…') : t('Export to Excel')}
                </button>
                <button
                  type="button"
                  className="lpi-reports__export-btn"
                  onClick={handleExportCsv}
                  disabled={isExportingExcel || isExportingCsv || isCalculating}
                >
                  {isExportingCsv ? t('Exporting…') : t('Export to CSV')}
                </button>
              </div>
            )}
          </div>
        )}

      

        {lpiResults && (
          <div className="lpi-reports__results-section">
            <LpiResultsTable
              orders={lpiResults.orders}
              filters={filters}
              pagination={pagination}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={isCalculating}
            />
          </div>
        )}

        {/* Hidden for the client demo (2026-09-15) — see the import above. */}
        {/* <LpiLookup /> */}
      </div>
    </div>
  );
};

export default LpiReports;
